import TextInput from '~/components/TextInput';
import EmailInput from '~/components/EmailInput';
import TextArea from '~/components/TextArea';
import Select from '~/components/Select';
import { useContext, useEffect, useState } from 'react';
import SectionPicker from '~/components/SectionPicker';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import MoneyInput from '~/components/MoneyInput';
import PrintableGiftCertificate from '~/components/giftcertificates/PrintableGiftCertificate';
import { addYears } from 'date-fns';
import { FormContext } from '~/components/forms/FormContext';
import ErrorMessage from '~/components/ErrorMessage';
import DateInput from '~/components/DateInput';
import { getBrowserTimezone } from '~/lib/DateTime';
import SendOnInput from '~/components/giftcertificates/SendOnInput';

export const DEFAULT_GIFT_CERTIFICATE_VALUES = ['25', '50', '75', '100'];

const BackgroundImages = [
    'https://images.unsplash.com/photo-1513151233558-d860c5398176?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxfHxiaXJ0aGRheSUyMGJhY2tncm91bmR8ZW58MHwwfHx8MTc0MDQ5MjQ1MHww&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1444159759392-aeeb3d5851c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxMHx8d2VsbG5lc3MlMjBiYWNrZ3JvdW5kfGVufDB8MHx8fDE3NDA0OTI3Mjd8MA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1515705576963-95cad62945b6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxMXx8YmlydGhkYXklMjBiYWNrZ3JvdW5kfGVufDB8MHx8fDE3NDA0OTI0NTB8MA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1516414559093-91c1c3d7359c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwyfHxsb3ZlJTIwYmFja2dyb3VuZHxlbnwwfDB8fHwxNzQwNDkyNTU1fDA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1519751138087-5bf79df62d5b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwxMnx8Z2lmdCUyMGNlcnRpZmljYXRlJTIwYmFja2dyb3VuZHxlbnwwfDB8fHwxNzQwNDkxMDM4fDA&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1480422080719-4d25a4c7e22c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w2NDM5ODR8MHwxfHNlYXJjaHwyNXx8aG9saWRheSUyMGJhY2tncm91bmR8ZW58MHwwfHx8MTc0MDQ5MjU4OHww&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1506102383123-c8ef1e872756?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=80',
    'https://images.unsplash.com/photo-1557517356-8b497896a6fb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=80',
];

const BackgroundImagePicker = ({ value, onChange, className }) => {
    return (
        <div
            className={`flex items-center space-x-4 overflow-y-scroll px-1 py-2 ${className}`}
        >
            {BackgroundImages.map(url => (
                <img
                    src={`${url}&w=150`}
                    onClick={() => onChange(value === url ? '' : url)}
                    className={`rounded-lg cursor-pointer ${value === url ? 'outline outline-2 outline-offset-2 themed-outline' : ''}`}
                />
            ))}
        </div>
    );
};

const AmountOptions = ({ settings, value, onChange, className }) => {
    const [selectedAmount, setSelectedAmount] = useState('');
    const [customAmount, setCustomAmount] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);

    const CUSTOM_VALUE = '';

    const amountOptions = (
        settings?.possibleValues || DEFAULT_GIFT_CERTIFICATE_VALUES
    )
        .filter(value => !!value)
        .map(value => ({
            label: formatCurrency(value),
            value: value,
        }));

    if (!settings?.disableCustom) {
        amountOptions.push({
            label: 'Custom',
            value: CUSTOM_VALUE,
        });
    }

    useEffect(() => {
        if (selectedAmount === CUSTOM_VALUE) {
            onChange(customAmount);
        } else {
            onChange(selectedAmount);
        }
    }, [selectedAmount, customAmount]);

    useEffect(() => {
        setHasLoaded(true);
    }, []);

    return (
        <div className={className}>
            <div className="input-label">Amount</div>
            <div className="sm:flex items-center">
                <SectionPicker
                    options={amountOptions}
                    value={selectedAmount}
                    onChange={setSelectedAmount}
                    className="mr-4 my-2"
                    slim={true}
                />
                {selectedAmount === CUSTOM_VALUE && (
                    <MoneyInput
                        value={customAmount}
                        onChange={setCustomAmount}
                        autoFocus={!customAmount && hasLoaded}
                    />
                )}
            </div>
        </div>
    );
};

const GiftCertificateQuestion = ({
    label,
    value: formValue,
    setValue: setFormValue,
    settings,
}) => {
    const [value, setValue] = useState({});
    const { form } = useContext(FormContext);

    // Only save off value if we have all the data
    useEffect(() => {
        if (value?.amount) {
            setFormValue({
                ...value,
                timezone: getBrowserTimezone(),
            });
        }
    }, [value]);

    const isError = !!settings?.isError;

    return (
        <div>
            <h3 className="text-center">{label}</h3>
            <PrintableGiftCertificate
                className="mb-4 scale-75"
                value={value?.amount || 0}
                recipient={{
                    name: value?.recipientName,
                    email: value?.recipientEmail,
                }}
                business={form?.business}
                message={value?.message}
                image={value?.image}
                code="ABCD1234"
                expires={
                    parseFloat(settings?.expires) > 0
                        ? addYears(new Date(), settings?.expires)
                        : ''
                }
            />

            <BackgroundImagePicker
                value={value?.image}
                onChange={value => setValue(old => ({ ...old, image: value }))}
                className="mb-4"
            />

            <TextInput
                onChange={value =>
                    setValue(old => ({ ...old, recipientName: value }))
                }
                value={value?.recipientName || ''}
                label="Recipient Name"
                className="mb-4"
                error={
                    isError &&
                    !value?.recipientName &&
                    'Recipient name is required'
                }
            />
            <EmailInput
                onChange={value =>
                    setValue(old => ({ ...old, recipientEmail: value }))
                }
                value={value?.recipientEmail || ''}
                label={'Recipient Email'}
                error={
                    !value?.recipientName &&
                    !!value?.send_at &&
                    'Email address is required'
                }
                className="mb-4"
            />
            <TextArea
                onChange={value =>
                    setValue(old => ({ ...old, message: value }))
                }
                value={value?.message || ''}
                label="Message"
                className="mb-4"
            />
            <div className="mb-4">
                <AmountOptions
                    onChange={value =>
                        setValue(old => ({ ...old, amount: value }))
                    }
                    value={value?.amount || ''}
                    settings={settings}
                />
                {isError && value?.amount <= 0 && (
                    <ErrorMessage>Please set an amount</ErrorMessage>
                )}
            </div>

            <SendOnInput
                value={value?.send_at}
                onChange={value => {
                    console.log('sendAt=', value);
                    setValue(old => ({ ...old, send_at: value }));
                }}
            />
        </div>
    );
};

export default GiftCertificateQuestion;
